import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LIST_PAGE_SIZE } from '../components/ui/ListPagination';

export const OFFER_REPORT_LIST_PAGE_KEY = 'cube.offerReport.listPage';
export const SEGMENTS_LIST_PAGE_KEY = 'cube.segments.listPage';
export const SEGMENT_TEMPLATES_LIST_PAGE_KEY = 'cube.segmentTemplates.listPage';
export const FAST_ID_REPORT_LIST_PAGE_KEY = 'cube.fastIdReport.listPage';

export function parseListPage(value) {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

export function listOffsetFromPage(page) {
  return (parseListPage(page) - 1) * LIST_PAGE_SIZE;
}

export function listPathWithPage(pathname, page) {
  const n = parseListPage(page);
  return n > 1 ? `${pathname}?page=${n}` : pathname;
}

export function readStoredListPage(storageKey) {
  if (!storageKey || typeof sessionStorage === 'undefined') return 1;
  try {
    return parseListPage(sessionStorage.getItem(storageKey));
  } catch {
    return 1;
  }
}

export function writeStoredListPage(storageKey, page) {
  if (!storageKey || typeof sessionStorage === 'undefined') return;
  try {
    const n = parseListPage(page);
    if (n > 1) sessionStorage.setItem(storageKey, String(n));
    else sessionStorage.removeItem(storageKey);
  } catch {
    /* ignore quota / private-mode failures */
  }
}

/** Back-link target: location.state first, then the last list page in this tab. */
export function listReturnPath(pathname, location, storageKey) {
  const fromState = parseListPage(location?.state?.listPage);
  if (fromState > 1) return listPathWithPage(pathname, fromState);
  const stored = readStoredListPage(storageKey);
  if (stored > 1) return listPathWithPage(pathname, stored);
  return pathname;
}

export function listPageState(page, stateKey = 'listPage') {
  return { [stateKey]: parseListPage(page) };
}

/**
 * Keeps list pagination in a query param (`?page=` by default) and mirrors it
 * to sessionStorage so detail-page back links can restore the same page
 * after a remount. Pass a distinct `paramName` for a second listview sharing
 * the same route (e.g. a tab), so the two pagination states don't collide.
 */
export function useListPage({ storageKey, paramName = 'page' } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromUrl = parseListPage(searchParams.get(paramName));
  const [listOffset, setListOffset] = useState(() => listOffsetFromPage(pageFromUrl));
  const currentPage = Math.floor(listOffset / LIST_PAGE_SIZE) + 1;

  const goToPage = useCallback(
    (page) => {
      const nextPage = parseListPage(page);
      setListOffset(listOffsetFromPage(nextPage));
      const next = new URLSearchParams(searchParams);
      if (nextPage <= 1) next.delete(paramName);
      else next.set(paramName, String(nextPage));
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true });
      }
    },
    [searchParams, setSearchParams, paramName]
  );

  const resetPage = useCallback(() => goToPage(1), [goToPage]);

  useEffect(() => {
    const expected = listOffsetFromPage(pageFromUrl);
    setListOffset((prev) => (prev === expected ? prev : expected));
  }, [pageFromUrl]);

  useEffect(() => {
    writeStoredListPage(storageKey, currentPage);
  }, [storageKey, currentPage]);

  return { listOffset, currentPage, goToPage, resetPage };
}
